import { Routes } from '@angular/router';
import { authGuard } from '@core/guards/auth.guard';
import { roleGuard } from '@core/guards/role.guard';
import { projectSelectedGuard } from '@core/guards/project-selected.guard';
import { INTERNAL_ROLES } from '@core/models/user.model';
import { dashboardAccessGuard } from '@features/nde/nde-results-project.guard';
import { legacyContextGuard, wellsMembershipFeatureGuard, wellsNptGuard } from '@core/guards/wells-context.guard';
import { wellsSupplierGuard, wellsSupplierIsolationGuard } from '@core/guards/wells-supplier.guard';

function redirectPath(path: string, destination: string): Routes {
  const [segment, ...remaining] = path.split('/');
  if (!remaining.length) {
    return [{ path: segment, pathMatch: 'full', redirectTo: `/${destination}` }];
  }
  return [{ path: segment, children: redirectPath(remaining.join('/'), destination) }];
}

function recordRouteAliases(
  prefixes: string[],
  destination: string,
  extras: Record<string, string> = {},
): Routes {
  return prefixes.map(prefix => ({
    path: prefix,
    children: [
      { path: '', pathMatch: 'full', redirectTo: `/${destination}` },
      { path: 'new', pathMatch: 'full', redirectTo: `/${destination}/new` },
      ...Object.entries(extras).flatMap(([path, target]) => redirectPath(path, target)),
      {
        path: ':id',
        children: [
          { path: '', pathMatch: 'full', redirectTo: `/${destination}/:id` },
          { path: 'edit', pathMatch: 'full', redirectTo: `/${destination}/:id/edit` },
        ],
      },
    ],
  }));
}

function isolateLegacyRoutes(config: Routes): Routes {
  return config.map(route => route.redirectTo !== undefined || ['wells-npt', 'wells-supplier', 'wells-memberships', 'login', 'login-failed', 'request-access', 'access-denied'].includes(route.path ?? '')
    ? route : { ...route, canMatch: [['projects', 'clear', 'switch-role'].includes(route.path ?? '') ? wellsSupplierIsolationGuard : legacyContextGuard, ...(route.canMatch ?? [])] });
}

export const routes: Routes = isolateLegacyRoutes([
  {
    path: 'wells-supplier', canActivate: [authGuard], canMatch: [wellsSupplierGuard],
    loadChildren: () => import('@features/wells-npt/wells-supplier.routes').then(module => module.WELLS_SUPPLIER_ROUTES),
  },
  {
    path: 'wells-memberships',
    canActivate: [authGuard, roleGuard(['admin'])],
    canMatch: [wellsMembershipFeatureGuard],
    loadComponent: () => import('@features/wells-npt/wells-membership.component').then(module => module.WellsMembershipComponent),
  },
  {
    path: 'wells-npt',
    canActivate: [authGuard],
    canMatch: [wellsNptGuard],
    loadChildren: () => import('@features/wells-npt/wells-npt.routes').then(module => module.WELLS_NPT_ROUTES),
  },
  // Login and access request routes do not require authentication.
  {
    path: 'login',
    loadComponent: () => import('@features/login/login.component').then(m => m.LoginComponent),
  },
  {
    path: 'login-failed',
    loadComponent: () => import('@features/login/login-failed.component').then(m => m.LoginFailedComponent),
  },
  {
    path: 'request-access',
    loadComponent: () => import('@features/access-request/access-request.component').then(m => m.AccessRequestComponent),
  },
  { path: 'access-denied', redirectTo: 'request-access' },
  { path: '', pathMatch: 'full', redirectTo: 'home' },
  {
    path: 'home',
    canActivate: [authGuard, roleGuard(INTERNAL_ROLES)],
    loadComponent: () => import('@features/home/home-dashboard.component').then(m => m.HomeDashboardComponent),
  },
  {
    path: 'search',
    canActivate: [authGuard, roleGuard(INTERNAL_ROLES)],
    loadComponent: () => import('@features/search/global-search.component').then(m => m.GlobalSearchComponent),
  },
  {
    path: 'activity',
    canActivate: [authGuard, roleGuard(INTERNAL_ROLES)],
    loadComponent: () => import('@features/activity-log/activity-log.component').then(m => m.ActivityLogComponent),
  },
  {
    path: 'projects',
    canActivate: [authGuard, roleGuard(INTERNAL_ROLES)],
    loadComponent: () => import('@features/projects/project-selector.component').then(m => m.ProjectSelectorComponent),
  },
  {
    path: 'projects/catalog',
    canActivate: [authGuard, roleGuard(INTERNAL_ROLES)],
    loadComponent: () => import('@features/projects/project-catalog-dashboard.component').then(m => m.ProjectCatalogDashboardComponent),
  },
  {
    path: 'projects/:id/members',
    canActivate: [authGuard, roleGuard(INTERNAL_ROLES)],
    loadComponent: () => import('@features/projects/project-members.component').then(m => m.ProjectMembersComponent),
  },
  {
    path: 'attachments/:id/download',
    canActivate: [authGuard],
    loadComponent: () => import('@shared/components/attachment-download/attachment-download.component').then(m => m.AttachmentDownloadComponent),
  },
  {
    path: 'portal',
    canActivate: [authGuard],
    loadChildren: () => import('@features/supplier-portal/portal.routes').then(m => m.PORTAL_ROUTES),
  },
  {
    path: 'dashboard/projects',
    pathMatch: 'full',
    redirectTo: 'dashboard?module=nde-results',
  },
  {
    path: 'dashboard',
    canActivate: [authGuard, dashboardAccessGuard],
    runGuardsAndResolvers: 'paramsOrQueryParamsChange',
    loadComponent: () => import('@features/nde/nde-results-page.component').then(module => module.NdeResultsPageComponent),
  },
  {
    path: 'my-assignments',
    canActivate: [authGuard, roleGuard(INTERNAL_ROLES), projectSelectedGuard],
    loadComponent: () => import('@features/my-assignments/my-assignments.component').then(m => m.MyAssignmentsComponent),
  },
  {
    path: 'quality',
    canActivate: [authGuard, roleGuard(INTERNAL_ROLES), projectSelectedGuard],
    loadComponent: () => import('@shared/components/hubs/quality-hub.component').then(m => m.QualityHubComponent),
  },
  {
    path: 'observations',
    canActivate: [authGuard, roleGuard(INTERNAL_ROLES), projectSelectedGuard],
    loadChildren: () => import('@features/quality-observations/quality-observations.routes').then(m => m.QUALITY_OBSERVATION_ROUTES),
  },
  {
    path: 'ncrs',
    canActivate: [authGuard, roleGuard([...INTERNAL_ROLES, 'inspector']), projectSelectedGuard],
    loadChildren: () => import('@features/nc-simplified/nc-simplified.routes').then(m => m.NC_SIMPLIFIED_ROUTES),
  },
  ...recordRouteAliases(['nc-enhanced', 'nc-simplified'], 'ncrs', { metrics: 'ncrs/metrics' }),
  {
    path: 'sqe',
    canActivate: [authGuard, roleGuard(INTERNAL_ROLES), projectSelectedGuard],
    loadChildren: () => import('@features/sqe/sqe.routes').then(m => m.SQE_ROUTES),
  },
  {
    path: 'capas',
    canActivate: [authGuard, roleGuard([...INTERNAL_ROLES, 'inspector']), projectSelectedGuard],
    loadChildren: () => import('@features/capa-simplified/capa-simplified.routes').then(m => m.CAPA_SIMPLIFIED_ROUTES),
  },
  ...recordRouteAliases(['capa-enhanced', 'capa-simplified'], 'capas', { metrics: 'capas/metrics' }),
  {
    path: 'nde',
    canActivate: [authGuard, roleGuard(INTERNAL_ROLES), projectSelectedGuard],
    loadChildren: () => import('@features/nde/nde.routes').then(module => module.NDE_ROUTES),
  },
  {
    path: 'nde-results-projects',
    pathMatch: 'full',
    redirectTo: 'dashboard/projects',
  },
  {
    path: 'nde-results',
    pathMatch: 'full',
    redirectTo: 'dashboard?module=nde-results',
  },
  {
    path: 'inspection-enhanced',
    canActivate: [authGuard, roleGuard([...INTERNAL_ROLES, 'inspector']), projectSelectedGuard],
    loadChildren: () => import('@features/inspection-enhanced/inspection-enhanced.routes').then(m => m.INSPECTION_ENHANCED_ROUTES),
  },
  ...recordRouteAliases(['inspections'], 'inspection-enhanced/packages', {
    metrics: 'inspection-enhanced/metrics',
  }),
  ...recordRouteAliases(['inspection-assignments'], 'inspection-enhanced/assignments', {
    metrics: 'inspection-enhanced/metrics',
    packages: 'inspection-enhanced/packages',
    'packages/new': 'inspection-enhanced/packages/new',
    'packages/:id': 'inspection-enhanced/packages/:id',
    'packages/:id/edit': 'inspection-enhanced/packages/:id/edit',
    nois: 'inspection-enhanced/assignments',
    reports: 'inspection-enhanced/reports',
  }),
  ...recordRouteAliases(['itps'], 'inspection-enhanced/itps'),
  { path: 'inspections-hub', pathMatch: 'full', redirectTo: 'inspection-enhanced' },
  {
    path: 'supply-chain',
    canActivate: [authGuard, roleGuard(INTERNAL_ROLES), projectSelectedGuard],
    loadComponent: () => import('@shared/components/hubs/supply-chain-hub.component').then(m => m.SupplyChainHubComponent),
  },
  {
    path: 'suppliers',
    canActivate: [authGuard, roleGuard(INTERNAL_ROLES), projectSelectedGuard],
    loadChildren: () => import('@features/suppliers/suppliers.routes').then(m => m.SUPPLIER_ROUTES),
  },
  {
    path: 'inspection-agencies',
    canActivate: [authGuard, roleGuard(INTERNAL_ROLES), projectSelectedGuard],
    loadChildren: () => import('@features/inspection-agencies/inspection-agencies.routes').then(m => m.INSPECTION_AGENCY_ROUTES),
  },
  {
    path: 'aml',
    canActivate: [authGuard, roleGuard(INTERNAL_ROLES), projectSelectedGuard],
    loadChildren: () => import('@features/aml/aml.routes').then(m => m.AML_ROUTES),
  },
  {
    path: 'training',
    canActivate: [authGuard, roleGuard(INTERNAL_ROLES), projectSelectedGuard],
    loadChildren: () => import('@features/training/training.routes').then(m => m.TRAINING_ROUTES),
  },
  {
    path: 'audits',
    canActivate: [authGuard, roleGuard(INTERNAL_ROLES), projectSelectedGuard],
    loadChildren: () => import('@features/audit-enhanced/audit-enhanced.routes').then(m => m.AUDIT_ENHANCED_ROUTES),
  },
  ...recordRouteAliases(['audit-enhanced', 'audits-classic'], 'audits', { metrics: 'audits/metrics' }),
  {
    path: 'risks',
    canActivate: [authGuard, roleGuard(INTERNAL_ROLES), projectSelectedGuard],
    loadChildren: () => import('@features/risks/risks.routes').then(m => m.RISK_ROUTES),
  },
  {
    path: 'documents',
    canActivate: [authGuard, roleGuard(INTERNAL_ROLES), projectSelectedGuard],
    loadChildren: () => import('@features/records-documents/documents.routes').then(m => m.DOCUMENT_ROUTES),
  },
  {
    path: 'glossary',
    canActivate: [authGuard],
    loadComponent: () => import('@features/glossary/glossary.component').then(m => m.GlossaryComponent),
  },
  {
    path: 'switch-role',
    canActivate: [authGuard],
    loadComponent: () => import('@features/admin/switch-role/switch-role.component').then(m => m.SwitchRoleComponent),
  },
  {
    path: 'quality-events',
    canActivate: [authGuard, roleGuard(INTERNAL_ROLES), projectSelectedGuard],
    loadChildren: () => import('@features/quality-events/quality-events.routes').then(m => m.QUALITY_EVENT_ROUTES),
  },
  {
    path: 'ofi',
    canActivate: [authGuard, roleGuard(INTERNAL_ROLES), projectSelectedGuard],
    loadChildren: () => import('@features/ofi/ofi.routes').then(m => m.OFI_ROUTES),
  },
  {
    path: 'clear',
    loadComponent: () => import('@features/admin/clear-cache.component').then(m => m.ClearCacheComponent),
  },
  { path: '**', redirectTo: 'home' },
]);
